import React from 'react';
import { CloudCog, Cloud, Check, Loader2, Circle, CircleDot, Settings, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserLibraryCategories } from '@/hooks/universal-storage/use-library-categories';
import { useItemCategoryMutations } from '@/hooks/universal-storage/use-library-item-categories';
import { useUniversalStorageItems } from '@/hooks/universal-storage/useUniversalLibraryItems';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

interface AddToLibraryButtonProps {
  itemId: string;
  contentType: 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image' | 'private_research' | 'private_page';
  forceWhiteText?: boolean;
  dropdownLabel?: string;
  variant?: 'default' | 'storage' | 'auto'; // Auto-detects context
}

const AddToLibraryButton: React.FC<AddToLibraryButtonProps> = ({ 
  itemId, 
  contentType, 
  forceWhiteText, 
  dropdownLabel = "Add to Storage",
  variant = 'auto'
}) => {
  const location = useLocation();
  const { openCategoryModal } = useGlobalModals();
  const { data: allCategories, isLoading: isLoadingCategories } = useUserLibraryCategories();
  const { data: storageItems, isLoading: isLoadingStorage } = useUniversalStorageItems();
  const { setCategory, isPending } = useItemCategoryMutations(itemId, contentType);
  
  // Find current category from storage data
  const currentStorageItem = storageItems?.find(
    item => {
      // Check the appropriate foreign key based on content type
      if (contentType === 'private_image' && item.workbook_image_id === itemId) return true;
      if (contentType === 'private_research' && item.workbook_research_id === itemId) return true;
      if (contentType === 'private_page' && item.workbook_page_id === itemId) return true;
      // For non-private content types, we don't have foreign keys in the new structure
      // These might need different handling depending on your implementation
      return false;
    }
  );
  const currentCategoryId = currentStorageItem?.category_id || null;

  // Smart context detection for optimal styling
  const isInStorageContext = variant === 'storage' || 
    (variant === 'auto' && location.pathname.includes('/storage'));

  // Determine button styling based on context and state
  const getButtonStyling = () => {
    if (isInStorageContext) {
      // Storage context: Same glass background, but force white icons for visibility
      return {
        bgClass: "bg-white/20 dark:bg-black/20", // Same as normal buttons
        borderClass: "border-white/20 dark:border-transparent", // Same as normal buttons
        iconColor: "text-white", // Always white for visibility in Storage
        shadow: "shadow-lg" // Same as normal buttons
      };
    } else {
      // Other contexts: Always white icons (no purple state)
      return {
        bgClass: "bg-white/20 dark:bg-black/20",
        borderClass: "border-white/20 dark:border-transparent",
        iconColor: forceWhiteText ? "text-white" : "text-gray-700 dark:text-white",
        shadow: "shadow-lg"
      };
    }
  };

  const styling = getButtonStyling();

  const handleCategorySelect = async (categoryId: string | null) => {
    if (isPending) return;
    await setCategory(categoryId);
  };

  const handleRemoveFromStorage = async () => {
    if (isPending) return;
    await setCategory(null);
  };
  
  const isAnythingLoading = isLoadingCategories || isLoadingStorage;
  
  // ✅ FIXED: Check if item is in storage (regardless of category)
  const isItemInStorage = currentStorageItem !== undefined;
  const isItemInAnyCategory = currentCategoryId !== null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "inline-flex items-center justify-center p-1 rounded-full",
              styling.bgClass,
              "border",
              styling.borderClass,
              styling.shadow,
              "backdrop-blur-lg",
              "transition-all duration-300",
              "hover:scale-105 active:scale-95"
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isItemInStorage ? (
              <CloudCog
                size={20}
                className={cn(
                  "transition-colors duration-300",
                  styling.iconColor
                )}
              />
            ) : (
              <Cloud
                size={20}
                className={cn(
                  "transition-colors duration-300",
                  styling.iconColor
                )}
              />
            )}
          </motion.button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isAnythingLoading ? (
            <div className="flex items-center justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            <>
              {/* Category options */}
              {allCategories && allCategories.length > 0 ? (
                <div className="max-h-60 overflow-y-auto">
                  {allCategories.map(cat => {
                    const isSelected = currentCategoryId === cat.id;
                    return (
                      <DropdownMenuItem 
                        key={cat.id} 
                        onSelect={(e) => e.preventDefault()} 
                        onClick={() => handleCategorySelect(cat.id)} 
                        disabled={isPending}
                        className="flex items-center gap-2"
                      >
                        {isSelected ? (
                          <CircleDot className="h-4 w-4 text-purple-500" />
                        ) : (
                          <Circle className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="flex-grow">{cat.name}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              ) : (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">No folders found.</p>
              )}
            </>
          )}
          
          {/* Remove from Storage option - only show if item is in storage */}
          {isItemInStorage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onSelect={(e) => { e.preventDefault(); handleRemoveFromStorage(); }}
                disabled={isPending}
                className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Remove</span>
              </DropdownMenuItem>
            </>
          )}
          
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openCategoryModal(); }}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Manage Storage</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default AddToLibraryButton;
